'use client';

import { type ComponentProps } from 'react';
import SanityImage from '@sanity-image';
import { useTranslations } from 'next-intl';
import LinkButton from 'components/controls/links/link-button';
import MarkdownParser from 'components/rich-text/markdown-parser';
import RedundantLink from 'components/accesibility/redundant-link';
import GridContainer from 'components/containers/grid-container';
import RichText from 'components/rich-text';
import { Eyebrow } from 'components/eyebrow';
import ChevronR from 'assets/icons/chevron-r.svg';
import { getUrlString } from 'helpers/helpers';
import type { SanityPromo } from '@metmuseum/types';
import styles from './featured-promos.module.scss';
import useAnimateIntoView from 'hooks/use-animate-into-view';
import cn from 'classnames';
import hoverStyles from '../../cards/hover-state.module.scss';
export function HomepageFeaturedPromos({
	promos,
	analytics,
}: HomepageFeaturedPromosProps) {
	return (
		<GridContainer
			columns={promos.length}
			shelfOnMobile={true}
			gap="var(--spacing-micro)"
		>
			{promos.map((promo) => (
				<HomepageFeaturedPromoCard
					promo={promo}
					analytics={analytics}
					key={promo.title}
				/>
			))}
		</GridContainer>
	);
}

function HomepageFeaturedPromoCard({
	promo,
	analytics,
}: HomepageFeaturedPromoCardProps) {
	const {
		ref: animateRef,
		className: animateClassName,
		style: animateStyle,
	} = useAnimateIntoView();
	const t = useTranslations();
	const promoLink = getUrlString(promo);
	const linkAnalytics = analytics?.Content_Block
		? {
				Content_Name: promo.title,
				Content_Block: analytics.Content_Block,
				Content_Type: 'promo',
				event: analytics.event || 'select_content',
			}
		: {};

	return (
		<div
			className={cn(styles.promoCard, animateClassName, hoverStyles.card)}
			ref={animateRef}
			style={animateStyle}
		>
			<div className={styles.imageWrapper}>
				<RedundantLink href={promoLink} {...linkAnalytics} />
				<figure>
					{promo.image && (
						<SanityImage
							image={promo.image}
							sizes={'(max-width: 500px) 90vw, (max-width: 960px) 45vw, 30vw'}
						/>
					)}
				</figure>
			</div>
			<div className={styles.infoContainer}>
				<div className={styles.info}>
					{promo.eyebrow && (
						<Eyebrow text={promo.eyebrow} className={styles.eyebrow} />
					)}
					<h4 className={styles.title}>
						<MarkdownParser>{promo.title}</MarkdownParser>
					</h4>
					<div className={styles.description}>
						<RichText value={promo.description} />
					</div>
				</div>
				{promoLink && (
					<LinkButton
						href={promoLink}
						analytics={linkAnalytics}
						rightIcon={<ChevronR />}
						className={styles.cta}
						text={promo.cta || t('readMore')}
					/>
				)}
			</div>
		</div>
	);
}

export interface HomepageFeaturedPromosProps {
	promos: SanityPromo[];
	analytics?: ComponentProps<typeof HomepageFeaturedPromoCard>['analytics'];
}

export interface HomepageFeaturedPromoCardProps {
	promo: SanityPromo;
	analytics?: ComponentProps<typeof LinkButton>['analytics'];
}
